// src/portal/portalCounterparty.js
//
// WHO IS ON THE OTHER SIDE OF THIS REVIEW, AND WHETHER TO SAY SO.
//
// One copy, used by the queue rows and by the detail header, because the
// interesting case is the one where the answer is NOTHING and two copies would
// disagree about it.
//
// ── THE INTRA-ORG CASE IS SILENCE, NOT A REPEATED NAME ──────────────────────
//
// owner_org_name_at_create and operator_org_name_at_create are equal for every
// review an org files for itself, which today is all but one row on this
// platform. Rendering them anyway puts "Earl O&G · submitted to Earl O&G" on
// every row of Earl's docket: it is TRUE, it is on screen, and it distinguishes
// nothing. A counterparty line that appears on every row cannot be read as
// "this one came from outside".
//
// So equal names render null, and the line's PRESENCE is the signal.
//
// ── THE NAMES ARE THE SNAPSHOTS, NOT THE ORGS' NAMES TODAY ─────────────────
//
// `_at_create` columns. A review filed against "Earl O&G" still says so after
// the org is renamed, because what is being described is a submission that
// happened, not a company that exists. The live name is available on route 4b
// for the picker, where the opposite is wanted.

/**
 * The counterparty for a QUEUE ROW, or null when there is none worth showing.
 *
 * The two sides want opposite halves of the same fact: a submitter's list is
 * theirs by construction and the question is where each one WENT; a reviewer's
 * list is their org's and the question is who it CAME FROM. Neither wants the
 * half it already knows -- that is the column that says nothing, one level up.
 *
 * @param {{owner_org_name_at_create?: string|null, operator_org_name_at_create?: string|null}} review
 * @param {boolean} isSubmitter
 * @returns {string|null}
 */
export function rowCounterparty(review, isSubmitter) {
  const owner = review?.owner_org_name_at_create;
  const operator = review?.operator_org_name_at_create;
  // Either name missing means the server did not tell us, which is not the same
  // as the two being equal. Both render nothing; only one is worth a comment.
  if (!owner || !operator) return null;
  if (owner === operator) return null;
  return isSubmitter ? `to ${owner}` : `from ${operator}`;
}

/**
 * The counterparty for the DETAIL HEADER, or null.
 *
 * Both names, in the direction the submission travelled, because the detail
 * page is where someone settles an argument about which company filed what
 * against whom. The queue is a list; this is the record.
 *
 * @param {{owner_org_name_at_create?: string|null, operator_org_name_at_create?: string|null}} review
 * @returns {string|null}
 */
export function detailCounterparty(review) {
  const owner = review?.owner_org_name_at_create;
  const operator = review?.operator_org_name_at_create;
  if (!owner || !operator) return null;
  if (owner === operator) return null;
  return `${operator} · submitted to ${owner}`;
}
